import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle';
import { faMinusCircle } from '@fortawesome/pro-solid-svg-icons/faMinusCircle';
import Icon from '@veeva/corgix/icon';
import { Option, Select } from '@veeva/corgix/select';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { BREAKPOINTS } from '../../../common/services/breakpoints';
import { AppStatsQAPlatformKey, VaultPlatformKey } from '../../services/FamilyConstants';
import CustomerStatFilterContext from './CustomerStatFilterContext';
import FilterSelectionMenu from './FilterSelectionMenu';
import FilterSelectionRowOperator from './FilterSelectionRowOperator';
import FilterStatType from './FilterStatType';
import {
    filterButtonsCSS,
    filterNameContainerCSS,
    filterNumberLabelCSS,
    filterNumberMenuContainerCSS,
    filterRowContainerCSS,
    filterRowDropDownMenuCSS,
    filterRowPlusButtonCSS,
    filterRowPlusMinusButtonsCSS,
    filterSelectionMenuContainerCSS,
    filterSelectionMenuMobileContainerCSS,
    filterSelectionMobileMenuOperatorCSS,
    filterTableFitRowCSS,
    filterTypeDropDownMenuCSS,
} from './styles/FilterSelectionDropdownMenuContainer.styles';
import PageGroupType from '../PageGroupType';

function isFilterStatTypeAllowed(statType, filterStatType) {
    if (statType === 'DAILY') {
        return filterStatType === 'DAILY' || filterStatType === 'SUM' || filterStatType === 'AVG';
    }
    if (statType === 'TOTAL') {
        return filterStatType === 'DAILY' || filterStatType === 'MAX';
    }
    if (statType === 'AVG') {
        return filterStatType === 'DAILY' || filterStatType === 'AVG';
    }
    return false;
}

function getShortenedStatLabel(label) {
    const splitLabel = label.split('>');
    return splitLabel[1]?.trim(); // get second part of label
}

function getFilterTypes(platformKey, pageGroup, allStats) {
    const allFilterTypes = [];
    let basicFilterTypes = ['Application', 'Customers'];
    const platformFilterTypes = ['Basics', 'Vault Size', 'POD'];
    const customerFilterTypes = [
        'Customer Region',
        'Customer Size',
        'Customer Industry',
        'Customer Segment',
    ];

    if (pageGroup === PageGroupType.ALL_CUSTOMERS) {
        allStats.forEach((stat) => {
            allFilterTypes.push(
                <Option key={stat} value={stat}>
                    {stat}
                </Option>,
            );
        });
    } else {
        if (platformKey === VaultPlatformKey || platformKey === AppStatsQAPlatformKey) {
            if (pageGroup === PageGroupType.PLATFORM) {
                basicFilterTypes.shift();
            }
            basicFilterTypes = basicFilterTypes.concat(platformFilterTypes);
        }

        allFilterTypes.push(
            <Option header key="instances" value="instances" disabled label="INSTANCE FIELDS" />,
        );
        basicFilterTypes.forEach((type) => {
            allFilterTypes.push(
                <Option key={type} value={type}>
                    {type}
                </Option>,
            );
        });

        allFilterTypes.push(
            <Option header key="customer" value="customer" disabled label="CUSTOMER FIELDS" />,
        );
        customerFilterTypes.forEach((type) => {
            allFilterTypes.push(
                <Option key={type} value={type}>
                    {type}
                </Option>,
            );
        });

        allFilterTypes.push(<Option header key="stats" value="stats" disabled label="STATS" />);
        allStats.forEach((stat) => {
            allFilterTypes.push(
                <Option
                    key={stat.compoundKey}
                    value={stat.compoundKey}
                    label={getShortenedStatLabel(stat.label)}
                >
                    {stat.label}
                </Option>,
            );
        });
    }

    return allFilterTypes;
}

export default function FilterSelectionRow({
    filterNumber,
    applications,
    customers,
    initialFilterType,
    filter,
    handleSetFilters,
    addFilterRow,
    deleteFilterRow,
    platformKey,
    totalSelectedRows,
    topDown,
    mobile,
    allStats,
}) {
    const { selectedCustomerFilterList, pageGroup, pendingFilterList } =
        useContext(CustomerStatFilterContext);

    const [filterType, setFilterType] = useState('');
    const [filterValue, setFilterValue] = useState('');
    const [filterStat, setFilterStat] = useState(null);
    const [operator, setOperator] = useState('');
    const [filterStatType, setFilterStatType] = useState(FilterStatType.DAILY);

    useEffect(() => {
        if (initialFilterType) {
            setFilterType(initialFilterType);
            if (initialFilterType.includes('Stats')) {
                const statKey = initialFilterType.split(' ')[1];
                const foundStat = allStats.find((stat) => stat.statKey === statKey);
                if (foundStat) {
                    setFilterValue(foundStat.compoundKey);
                    setFilterStat(foundStat);
                    setFilterStatType(
                        isFilterStatTypeAllowed(foundStat.type, filterStatType)
                            ? filterStatType
                            : FilterStatType.DAILY,
                    );
                }
            } else {
                setFilterValue(initialFilterType);
            }
        } else {
            setFilterType('');
            setFilterValue('');
        }
    }, [initialFilterType, allStats, filterStatType]);

    const handleSelectFilterTypeOption = (e, selectedFilter) => {
        if (selectedFilter.includes('_')) {
            const foundStat = allStats.find((stat) => stat.compoundKey === selectedFilter);
            if (foundStat) {
                setFilterValue(selectedFilter);
                setFilterType(`Stats ${foundStat.statKey}`);
                handleSetFilters(filterNumber, null, true, `Stats ${foundStat.statKey}`);
                setFilterStat(foundStat);
            }
        } else {
            setFilterType(selectedFilter);
            setFilterValue(selectedFilter);
            handleSetFilters(filterNumber, null, true, selectedFilter);
        }
    };

    const handlePlusClick = () => {
        addFilterRow(filterNumber);
    };
    const handleMinusClick = () => {
        deleteFilterRow(filterNumber);
    };

    const isAboveMediumScreenThreshold = useMediaQuery({ minWidth: BREAKPOINTS.EXTRA_SMALL.max });

    return (
        <tr css={filterType ? filterNameContainerCSS : filterTableFitRowCSS}>
            <td>
                <div css={filterRowContainerCSS}>
                    <div css={filterNumberMenuContainerCSS}>
                        <div css={filterNumberLabelCSS}>{filterNumber}</div>
                        <div css={filterRowDropDownMenuCSS}>
                            <Select
                                value={filterValue}
                                onChange={handleSelectFilterTypeOption}
                                disabled={selectedCustomerFilterList === 'redReport'}
                                css={filterTypeDropDownMenuCSS}
                            >
                                {getFilterTypes(platformKey, pageGroup, allStats)}
                            </Select>
                        </div>
                    </div>
                    {filterType && (!mobile || isAboveMediumScreenThreshold) ? (
                        <>
                            <FilterSelectionRowOperator
                                filterType={filterType}
                                filterRowId={filterNumber}
                                handleSetFilters={handleSetFilters}
                                filter={filter}
                                filterStat={filterStat}
                                operator={operator}
                                setOperator={setOperator}
                                filterStatType={filterStatType}
                                disabled={selectedCustomerFilterList === 'redReport'}
                            />
                            <div css={filterSelectionMenuContainerCSS}>
                                <FilterSelectionMenu
                                    filterRowId={filterNumber}
                                    filterType={filterType}
                                    filterStat={filterStat}
                                    handleSetFilters={handleSetFilters}
                                    applications={applications}
                                    customers={customers}
                                    filter={filter}
                                    topDown={topDown}
                                    operator={operator}
                                    filterStatType={filterStatType}
                                    setFilterStatType={setFilterStatType}
                                    disabled={selectedCustomerFilterList === 'redReport'}
                                />
                            </div>
                        </>
                    ) : null}
                    {selectedCustomerFilterList === 'redReport' ? null : (
                        <div css={filterRowPlusMinusButtonsCSS}>
                            <div css={filterRowPlusButtonCSS}>
                                <button
                                    onClick={handleMinusClick}
                                    type="button"
                                    css={filterButtonsCSS}
                                    disabled={pendingFilterList.length === 0}
                                >
                                    <Icon size="xs" type={faMinusCircle} />
                                </button>
                            </div>
                            <div css={filterRowPlusButtonCSS}>
                                <button
                                    onClick={handlePlusClick}
                                    type="button"
                                    css={filterButtonsCSS}
                                    disabled={pendingFilterList.length < totalSelectedRows}
                                >
                                    <Icon size="xs" type={faPlusCircle} />
                                </button>
                            </div>
                        </div>
                    )}
                    {filterType && mobile && !isAboveMediumScreenThreshold ? (
                        <>
                            <div css={filterSelectionMobileMenuOperatorCSS}>
                                <FilterSelectionRowOperator
                                    filterType={filterType}
                                    filterRowId={filterNumber}
                                    handleSetFilters={handleSetFilters}
                                    filter={filter}
                                    filterStat={filterStat}
                                    operator={operator}
                                    setOperator={setOperator}
                                    filterStatType={filterStatType}
                                    disabled={selectedCustomerFilterList === 'redReport'}
                                />
                            </div>
                            <div css={filterSelectionMenuMobileContainerCSS}>
                                <FilterSelectionMenu
                                    filterRowId={filterNumber}
                                    filterType={filterType}
                                    filterStat={filterStat}
                                    handleSetFilters={handleSetFilters}
                                    applications={applications}
                                    customers={customers}
                                    filter={filter}
                                    topDown={topDown}
                                    operator={operator}
                                    filterStatType={filterStatType}
                                    setFilterStatType={setFilterStatType}
                                    disabled={selectedCustomerFilterList === 'redReport'}
                                />
                            </div>
                        </>
                    ) : null}
                </div>
            </td>
        </tr>
    );
}
FilterSelectionRow.propTypes = {
    addFilterRow: PropTypes.func,
    allStats: PropTypes.arrayOf(
        PropTypes.shape({
            compoundKey: PropTypes.string,
            label: PropTypes.string,
            statKey: PropTypes.string,
            type: PropTypes.string,
        }),
    ),
    applications: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string,
            label: PropTypes.string,
            stats: PropTypes.arrayOf(
                PropTypes.shape({
                    applicationKey: PropTypes.string,
                    key: PropTypes.string,
                    label: PropTypes.string,
                }),
            ),
        }),
    ),
    customers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            label: PropTypes.string,
        }),
    ),
    deleteFilterRow: PropTypes.func,
    filter: PropTypes.shape({
        appKeys: PropTypes.arrayOf(PropTypes.string),
        customerIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
        customerSizes: PropTypes.arrayOf(PropTypes.string),
        industries: PropTypes.arrayOf(PropTypes.string),
        podNames: PropTypes.arrayOf(PropTypes.string),
        regions: PropTypes.arrayOf(PropTypes.string),
        segments: PropTypes.arrayOf(PropTypes.string),
        vaultBasics: PropTypes.string,
        vaultSizes: PropTypes.arrayOf(PropTypes.string),
    }),
    filterNumber: PropTypes.number,
    handleSetFilters: PropTypes.func,
    initialFilterType: PropTypes.string,
    mobile: PropTypes.bool,
    platformKey: PropTypes.string,
    topDown: PropTypes.bool,
    totalSelectedRows: PropTypes.number.isRequired,
};
